import { StateCreator } from "zustand";
import { JobItemEntityType } from "@/lib/supabase/supabaseTypes";
import { produce } from "immer";
import { JobStoreUnion } from ".";

interface HistoryState {
	past: JobItemEntityType[][];
	future: JobItemEntityType[][];
	currentJobDocumentId: number | null;
}

export interface JobItemsHistorySlice extends HistoryState {
	pushToHistory: (jobDocumentId: number, currentSnapshot: JobItemEntityType[]) => void;
	performUndo: (jobDocumentId: number, currentSnapshot: JobItemEntityType[]) => JobItemEntityType[] | null;
	performRedo: (jobDocumentId: number, currentSnapshot: JobItemEntityType[]) => JobItemEntityType[] | null;
	clearHistory: () => void;
	canUndo: () => boolean;
	canRedo: () => boolean;
}

const MAX_HISTORY_SIZE = 20;

export const createJobItemsHistoryStore: StateCreator<
	JobStoreUnion,
	[],
	[],
	JobItemsHistorySlice
> = (set, get) => ({
	past: [],
	future: [],
	currentJobDocumentId: null,

	pushToHistory: (jobDocumentId: number, currentSnapshot: JobItemEntityType[]) => {
		set(
			produce((state: JobItemsHistorySlice) => {
				if (state.currentJobDocumentId !== jobDocumentId) {
					state.past = [];
					state.future = [];
					state.currentJobDocumentId = jobDocumentId;
				}

				state.past.push([...currentSnapshot]);
				state.future = [];

				if (state.past.length > MAX_HISTORY_SIZE) {
					state.past.shift();
				}
			})
		);
	},

	performUndo: (jobDocumentId: number, currentSnapshot: JobItemEntityType[]) => {
		const state = get();

		if (state.currentJobDocumentId !== jobDocumentId || state.past.length === 0) {
			return null;
		}

		const previousState = state.past[state.past.length - 1];

		set(
			produce((state: JobItemsHistorySlice) => {
				state.past.pop();
				state.future.push([...currentSnapshot]);
			})
		);

		return previousState;
	},

	performRedo: (jobDocumentId: number, currentSnapshot: JobItemEntityType[]) => {
		const state = get();

		if (state.currentJobDocumentId !== jobDocumentId || state.future.length === 0) {
			return null;
		}

		const nextState = state.future[state.future.length - 1];

		set(
			produce((state: JobItemsHistorySlice) => {
				state.future.pop();
				state.past.push([...currentSnapshot]);
			})
		);

		return nextState;
	},

	clearHistory: () => {
		set(
			produce((state: JobItemsHistorySlice) => {
				state.past = [];
				state.future = [];
				state.currentJobDocumentId = null;
			})
		);
	},

	canUndo: () => {
		const state = get();
		return state.past.length > 0;
	},

	canRedo: () => {
		const state = get();
		return state.future.length > 0;
	},
});
